import React, { type FC, useEffect, useMemo, useRef } from "react";
import styles from "./SpecsAndHighlights.module.scss";
import Text from "../../../../shared-components/css-modules/lexus/atoms/Text";
import BackButton from "../common/header/BackButton";
import SpecsDetail from "./SpecsDetail";
import { useAphBreakpoint } from "../../../../shared-logic/hooks/useBreakpoint";
import Container from "../../../../shared-components/css-modules/lexus/atoms/Container";
import SpecsNavigationContainer from "./SpecsNavigationContainer";
import HighlightsSlider from "./HighlightsSlider/HighlightsSlider";
import { useCarConfigSelector } from "../../../redux/store";
import HighlightsHero from "./HighlightsHero";
import { CarConfigLabel, useCarConfigLabel } from "../../../utils/labelUtils";
import useSectionIntersectionObserver from "../../../hooks/useSectionIntersectionObserver";
import { useSpecs } from "../../../hooks/useSpecs";
import type { SpecsNavigationItemType } from "../CarConfigBase";

export const HIGHLIGHTS_ID = "highlights-top";

type SpecsAndHighlightsProps = {
    SpecsNavigationItems: SpecsNavigationItemType[];
};

const SpecsAndHighlights: FC<SpecsAndHighlightsProps> = () => {
    const isMobile = useAphBreakpoint("down", "xl");
    const specs = useSpecs();
    const gradeFeatures =
        useCarConfigSelector((state) =>
            state.grades.items.find((grade) => grade.id === state.car.grade.id),
        )?.gradeFeatures.filter(({ valid }) => valid) || [];
    const apheleiaHighlightsPageUrl = useCarConfigSelector(
        (state) => state.carConfigSettings.apheleiaHighlightsPageUrl,
    );
    const sectionRefs = useRef(new Map());
    const highlightsLabel = useCarConfigLabel("carConfigHighlights");

    const specsNavigationItems = useMemo(() => {
        return [{ id: HIGHLIGHTS_ID, name: highlightsLabel }, ...specs];
    }, [specs, highlightsLabel]);

    useEffect(() => {
        specsNavigationItems.forEach((item) => {
            if (!sectionRefs.current.has(item.id)) {
                sectionRefs.current.set(item.id, React.createRef());
            }
        });

        const currentItemIds = new Set(specsNavigationItems.map((item) => item.id));
        sectionRefs.current.forEach((ref, itemId) => {
            if (!currentItemIds.has(itemId)) {
                sectionRefs.current.delete(itemId);
            }
        });
    }, [specsNavigationItems]);

    useSectionIntersectionObserver(sectionRefs.current);

    return (
        <div id="tab-specs-and-highlights">
            <main data-testid="specs-and-highlights-step">
                <Container className={styles.container}>
                    <SpecsNavigationContainer hideNavigation={isMobile} specsNavigationItems={specsNavigationItems} />
                    <section className={styles.contentContainer}>
                        <BackButton sizing="xs" />
                        {gradeFeatures.length > 0 && apheleiaHighlightsPageUrl ? (
                            <div id={HIGHLIGHTS_ID} ref={sectionRefs.current.get(HIGHLIGHTS_ID)}>
                                <Text variant="apheleia-h6" color="grey-platinum-dark">
                                    <CarConfigLabel label="carConfigHighlights" />
                                </Text>

                                <HighlightsSlider gradeFeatures={gradeFeatures} />
                            </div>
                        ) : (
                            <div id={HIGHLIGHTS_ID} ref={sectionRefs.current.get(HIGHLIGHTS_ID)}>
                                <HighlightsHero />
                            </div>
                        )}

                        <div className={styles.specs}>
                            {specs.map((spec, index) => (
                                <section
                                    key={spec.id}
                                    id={spec.id}
                                    ref={sectionRefs.current.get(spec.id)}
                                    aria-label={spec.name}
                                    className={styles.specSection}
                                >
                                    {index === 0 && (
                                        <Text
                                            variant="apheleia-h6"
                                            color="grey-platinum-dark"
                                            className={styles.specsTitle}
                                        >
                                            <CarConfigLabel label="carConfigSpecs" />
                                        </Text>
                                    )}
                                    <Text tag="h3" variant="apheleia-h4">
                                        {spec.name}
                                    </Text>
                                    <SpecsDetail group={spec} />
                                </section>
                            ))}
                        </div>
                    </section>
                </Container>
            </main>
        </div>
    );
};

export default SpecsAndHighlights;
